import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, PhoneCall, MousePointerClick, Repeat } from "lucide-react";
import { smoothScrollTo } from "../../hooks/useLenis";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/303a5a44-da73-4a39-8545-fc202ecc44fd/images/5e7b375d3eeca875c1ac1bfc1204c8bc7efe49f89f09fb6519ee8acf9d8e9269.png";

/**
 * Word-by-word masked reveal driven by a container that staggers children.
 * Each word rides up from behind a clipping mask so the type feels typeset.
 */
const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.06, delayChildren: 0.15 },
  },
};

const wordMask = {
  hidden: {},
  show: {},
};

const word = {
  hidden: { y: "110%", opacity: 0 },
  show: {
    y: "0%",
    opacity: 1,
    transition: {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1], // cubic-bezier easeOutExpo-ish
    },
  },
};

function MaskedLine({ children, className = "", delay = 0 }) {
  const words = String(children).split(" ");
  return (
    <motion.span
      className={`block ${className}`}
      variants={{
        hidden: {},
        show: {
          transition: {
            staggerChildren: 0.055,
            delayChildren: delay,
          },
        },
      }}
    >
      {words.map((w, i) => (
        <span
          key={`${w}-${i}`}
          className="inline-flex overflow-hidden align-baseline"
          style={{ paddingBottom: "0.12em", marginRight: "0.28em" }}
        >
          <motion.span variants={word} className="inline-block will-change-transform">
            {w}
          </motion.span>
        </span>
      ))}
    </motion.span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  // Parallax layers
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);
  const gridY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  const goTo = (id) => (e) => {
    e.preventDefault();
    smoothScrollTo(id, -60);
  };

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-40 pb-24 sm:pt-52 sm:pb-32 min-h-[92vh]"
    >
      {/* Parallax background image */}
      <motion.div
        aria-hidden
        style={{ y: bgY, scale: bgScale, backgroundImage: `url(${HERO_BG})` }}
        className="absolute inset-0 bg-cover bg-center opacity-[0.20]"
      />
      {/* Gradient fade */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(2,6,23,0.35) 0%, rgba(2,6,23,0.85) 60%, #020617 100%)",
        }}
      />
      {/* Parallax grid */}
      <motion.div
        aria-hidden
        style={{ y: gridY }}
        className="absolute inset-0 opacity-[0.07]"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </motion.div>
      {/* Signature accent — spotlight glow behind headline */}
      <div
        aria-hidden
        className="absolute -top-20 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-[0.12] pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, #7C93FF 0%, rgba(124,147,255,0.15) 30%, transparent 60%)",
        }}
      />

      <motion.div
        style={{ y: contentY }}
        className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-24"
      >
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="text-xs tracking-[0.35em] uppercase text-slate-400 font-bold mb-10 flex items-center gap-3"
          data-testid="hero-overline"
        >
          <span className="inline-block w-8 h-px bg-slate-500" />
          AI lead engagement for private health clinics
        </motion.p>

        <motion.h1
          initial="hidden"
          animate="show"
          variants={container}
          className="font-heading text-[2.75rem] sm:text-6xl lg:text-[5.25rem] tracking-tighter font-semibold text-white max-w-5xl leading-[1.02]"
          data-testid="hero-headline"
        >
          <MaskedLine className="text-white">
            “Private clinics don't lose
          </MaskedLine>
          <MaskedLine className="text-white" delay={0.06}>
            patients to better clinicians.
          </MaskedLine>
          <MaskedLine className="text-slate-500" delay={0.12}>
            They lose them to faster
          </MaskedLine>
          <MaskedLine className="text-slate-500" delay={0.18}>
            engagement.”
          </MaskedLine>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-10 text-base sm:text-lg leading-relaxed text-slate-400 max-w-2xl"
          data-testid="hero-subheadline"
        >
          ProCliniq helps seal leaks in the patient journey through instant
          voice receptionist engagement.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.28 }}
          className="mt-10 max-w-2xl border-l-2 border-white/30 pl-5"
          data-testid="hero-front-desk-callout"
        >
          <p className="text-xs tracking-[0.25em] uppercase text-slate-300 font-bold">
            Built for the front desk you wish you had
          </p>
          <p className="mt-2 text-sm sm:text-base text-slate-400 leading-relaxed">
            Replies during ongoing clinics, weekends, and the 5pm rush —
            without hiring another receptionist.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4 }}
          className="mt-12 flex flex-col sm:flex-row gap-3"
        >
          <a
            href="#book"
            onClick={goTo("book")}
            data-testid="hero-primary-cta"
            className="group inline-flex items-center justify-center gap-2 bg-white text-slate-950 hover:bg-slate-200 transition-colors font-medium rounded-sm px-6 py-3.5 text-sm"
          >
            Book a call to learn how it works
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="#services"
            onClick={goTo("services")}
            data-testid="hero-secondary-cta"
            className="inline-flex items-center justify-center bg-transparent border border-white/20 text-white hover:bg-white/5 hover:border-white/40 transition-colors rounded-sm px-6 py-3.5 text-sm"
          >
            See the services
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.0, delay: 1.55 }}
          className="mt-20 grid grid-cols-1 sm:grid-cols-3 border-t border-white/10"
        >
          {[
            { icon: PhoneCall, label: "Live Lead Engagement" },
            { icon: MousePointerClick, label: "Website Widget" },
            { icon: Repeat, label: "Lead Reactivation (SMS)" },
          ].map((item, i) => (
            <div
              key={item.label}
              data-testid={`hero-capability-${i}`}
              className={`flex items-center gap-3 py-6 ${
                i > 0 ? "sm:border-l border-white/10 sm:pl-8" : ""
              }`}
            >
              <item.icon className="w-4 h-4 text-slate-300" />
              <span className="text-sm text-slate-300 tracking-tight">
                {item.label}
              </span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
